import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { errorMessage, registerPasskey } from '../../lib/passkey.js';
import { Alert, Spinner } from '../../lib/ui.js';
import { t } from '../../i18n/index.js';

type InviteInfo = {
  state: 'valid' | 'used' | 'expired' | 'revoked' | 'missing';
  name?: string;
  purpose?: string;
};

export function InvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    api<InviteInfo>(`/auth/invite/${encodeURIComponent(token)}`)
      .then(setInfo)
      .catch(() => setInfo({ state: 'missing' }));
  }, [token]);

  if (!info) return <Spinner />;
  if (done) {
    return (
      <section className="card">
        <h2>{t('invite_done', { name: done })}</h2>
        <p className="muted">{t('invite_next_step')}</p>
        <button
          type="button"
          // The bank details step comes next; it is skippable and hands on to the home-screen
          // step, or straight to the punch screen if the app is already there.
          onClick={() => navigate('/bank-details', { replace: true })}
        >
          {t('continue')}
        </button>
      </section>
    );
  }
  if (info.state !== 'valid') {
    const key = `invite_${info.state}` as const;
    // Every dead link says what to do next: an old or cancelled one names the admin, a link that
    // has already been used points at the sign-in screen, which is what that worker wants.
    const used = info.state === 'used';
    return (
      <section className="card">
        <Alert>{t(key)}</Alert>
        <p className="muted">{used ? t('invite_used_help') : t('invite_missing_help')}</p>
        {used && (
          <button type="button" onClick={() => navigate('/', { replace: true })}>
            {t('invite_sign_in')}
          </button>
        )}
      </section>
    );
  }

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const path = `/auth/invite/${encodeURIComponent(token)}`;
      const r = await registerPasskey<{ ok: boolean; name: string }>(
        `${path}/options`,
        `${path}/verify`,
      );
      await refresh();
      setDone(r.name);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2>{t('invite_title', { name: info.name ?? '' })}</h2>
      <p>{info.purpose === 'reregister' ? t('invite_reregister_intro') : t('invite_intro')}</p>
      <p className="muted">{t('invite_location_note')}</p>
      {error && <Alert>{error}</Alert>}
      <button type="button" className="big" onClick={start} disabled={busy}>
        {busy ? t('passkey_setting_up') : t('passkey_set_up')}
      </button>
    </section>
  );
}
